import axios from 'axios';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Container from 'react-bootstrap/Container';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { api } from '../Api';
import { Store } from '../Store';
import { getError, otpProviderLabel } from '../utils';

const statusColor = (s) => ({
  used: { bg: '#dcfce7', color: '#166534', label: 'Verified' },
  pending: { bg: '#dbeafe', color: '#1d4ed8', label: 'Sent' },
  cancelled: { bg: '#fee2e2', color: '#991b1b', label: 'Cancelled' },
  expired: { bg: '#f3f4f6', color: '#374151', label: 'Expired' },
}[s] || { bg: '#f3f4f6', color: '#374151', label: s });

export default function UserOtpHistoryScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const headers = useMemo(() => ({ Authorization: `Bearer ${userInfo?.token}` }), [userInfo]);

  const [data, setData] = useState({ rows: [], page: 1, pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(api + `/api/developer/my-otp-history?page=${page}`, { headers });
      setData(res);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoading(false);
    }
  }, [page, headers]);

  useEffect(() => { load(); }, [load]);

  return (
    <Container className="no-gutter">
      <Helmet><title>OTP History</title></Helmet>
      <div className="card" style={{ marginTop: 12 }}>
        <div className="card-header">
          <Link to="/history" style={{ textDecoration: 'none' }}>
            <i className="fa fa-long-arrow-left" aria-hidden="true"></i>{'  '}
          </Link>
          OTP History
        </div>
        <div className="card-body">
          {loading ? (
            <p className="text-center text-muted my-4">Loading…</p>
          ) : data.rows.length === 0 ? (
            <p className="text-center text-muted my-4">You have no OTP records yet.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Date</th>
                    <th style={s.th}>For</th>
                    <th style={s.th}>Status</th>
                    <th style={s.th}>Charge</th>
                    <th style={s.th}>Sent via</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => {
                    const sc = statusColor(r.status);
                    return (
                      <tr key={r._id}>
                        <td style={s.td}>{new Date(r.createdAt).toLocaleString()}</td>
                        <td style={s.td}>{r.type}</td>
                        <td style={s.td}><span style={{ ...s.pill, background: sc.bg, color: sc.color }}>{sc.label}</span></td>
                        <td style={s.td}>{r.userCharge ? `৳${Number(r.userCharge).toFixed(2)}` : '—'}</td>
                        <td style={s.td}>
                          {(r.channels || [])
                            .filter((c) => c.ok || c.mayHaveDelivered)
                            .map((c) => otpProviderLabel(c.provider))
                            .join(', ') || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {data.pages > 1 && (
            <div className="text-center mt-3">
              <button className="btn btn-sm btn-outline-secondary me-2" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Prev</button>
              <span className="text-muted">Page {data.page} of {data.pages}</span>
              <button className="btn btn-sm btn-outline-secondary ms-2" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}>Next ›</button>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
}

const s = {
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 480 },
  th: { textAlign: 'left', padding: '9px 10px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 11, textTransform: 'uppercase', color: '#6b7280' },
  td: { padding: '9px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 13 },
  pill: { borderRadius: 999, padding: '2px 9px', fontSize: 12, fontWeight: 700 },
};
